/**
 * WHO / CDC Growth Reference Data
 * ────────────────────────────────────────────────────────────
 * LMS parameters for weight-for-age, length-for-age, head-
 * circumference-for-age, and BMI-for-age, by sex, 0-36 months
 * (WHO Child Growth Standards).
 *
 * LMS percentile computation:
 *   Z = ((value / M)^L - 1) / (L * S)   when L != 0
 *   Z = ln(value / M) / S                when L == 0
 *   percentile = Φ(Z) * 100              (normal CDF)
 *
 * Data points sampled at clinically meaningful ages (6, 12, 18,
 * 24, 30, 36 months) — the panel interpolates between them
 * linearly on the LMS parameters. Accurate enough for demo;
 * real integration can load the full WHO tables.
 *
 * Units: weight (kg), length (cm), head circumference (cm),
 * BMI (kg/m²).
 */

/* age_months : [L, M, S] */
const W_BOYS = {
  6:  [ 0.0029, 7.9340, 0.11911],
  12: [-0.3130, 9.6479, 0.11711],
  18: [-0.1800, 10.9418, 0.11727],
  24: [-0.0651, 12.1515, 0.11768],
  30: [ 0.0388, 13.2830, 0.11824],
  36: [ 0.1324, 14.3395, 0.11889],
};
const W_GIRLS = {
  6:  [-0.0119, 7.2970, 0.12095],
  12: [-0.0631, 8.9481, 0.12152],
  18: [-0.0001, 10.2315, 0.12232],
  24: [ 0.0620, 11.4775, 0.12329],
  30: [ 0.1213, 12.6697, 0.12440],
  36: [ 0.1784, 13.8074, 0.12560],
};
const L_BOYS = {
  6:  [1, 67.6229, 0.03408],
  12: [1, 75.7488, 0.03530],
  18: [1, 81.9725, 0.03610],
  24: [1, 87.0655, 0.03666],
  30: [1, 91.3849, 0.03700],
  36: [1, 95.2714, 0.03728],
};
const L_GIRLS = {
  6:  [1, 65.7311, 0.03500],
  12: [1, 74.0145, 0.03640],
  18: [1, 80.7079, 0.03750],
  24: [1, 85.7153, 0.03810],
  30: [1, 90.2659, 0.03848],
  36: [1, 94.1732, 0.03873],
};
const HC_BOYS = {
  6:  [1, 43.9781, 0.02989],
  12: [1, 46.0256, 0.03183],
  18: [1, 47.2768, 0.03272],
  24: [1, 48.2650, 0.03334],
  30: [1, 48.9892, 0.03371],
  36: [1, 49.5930, 0.03399],
};
const HC_GIRLS = {
  6:  [1, 42.8329, 0.03165],
  12: [1, 44.8812, 0.03338],
  18: [1, 46.1529, 0.03435],
  24: [1, 47.1572, 0.03500],
  30: [1, 47.8855, 0.03540],
  36: [1, 48.5260, 0.03566],
};
const BMI_BOYS = {
  6:  [-0.2147, 17.2560, 0.08658],
  12: [-0.2526, 17.2462, 0.08715],
  18: [-0.2753, 16.4780, 0.08687],
  24: [-0.2791, 15.9414, 0.08799],
  30: [-0.2794, 15.7330, 0.08838],
  36: [-0.2767, 15.6015, 0.08869],
};
const BMI_GIRLS = {
  6:  [-0.0490, 16.7272, 0.09070],
  12: [-0.0808, 16.8943, 0.09093],
  18: [-0.1069, 16.1384, 0.09079],
  24: [-0.1150, 15.7517, 0.09150],
  30: [-0.1208, 15.7001, 0.09200],
  36: [-0.1262, 15.6955, 0.09241],
};

const TABLES = {
  weight:  { male: W_BOYS,  female: W_GIRLS },
  length:  { male: L_BOYS,  female: L_GIRLS },
  hc:      { male: HC_BOYS, female: HC_GIRLS },
  bmi:     { male: BMI_BOYS, female: BMI_GIRLS },
};

/* Abramowitz & Stegun approximation of the standard-normal CDF. */
const erf = (x) => {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741,
        a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return sign * y;
};
const normalCdf = (z) => 0.5 * (1 + erf(z / Math.SQRT2));

const interpolateLMS = (table, ageMonths) => {
  const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (ageMonths <= keys[0]) return table[keys[0]];
  if (ageMonths >= keys[keys.length - 1]) return table[keys[keys.length - 1]];
  let lo = keys[0], hi = keys[keys.length - 1];
  for (let i = 0; i < keys.length - 1; i++) {
    if (ageMonths >= keys[i] && ageMonths <= keys[i + 1]) { lo = keys[i]; hi = keys[i + 1]; break; }
  }
  const f = (ageMonths - lo) / (hi - lo);
  const [L1, M1, S1] = table[lo];
  const [L2, M2, S2] = table[hi];
  return [L1 + (L2 - L1) * f, M1 + (M2 - M1) * f, S1 + (S2 - S1) * f];
};

/**
 * Compute WHO/CDC percentile for one measurement.
 *   metric : 'weight' | 'length' | 'hc' | 'bmi'
 *   sex    : 'male'  | 'female'
 *   ageMonths, value (units per metric)
 *
 * Returns { z, percentile, L, M, S }. Percentile is 0–100.
 */
export const percentile = (metric, sex, ageMonths, value) => {
  const tbl = TABLES[metric]?.[sex];
  if (!tbl || !value) return { z: 0, percentile: 0, L: 0, M: 0, S: 0 };
  const [L, M, S] = interpolateLMS(tbl, ageMonths);
  const z = L !== 0
    ? (Math.pow(value / M, L) - 1) / (L * S)
    : Math.log(value / M) / S;
  const pct = normalCdf(z) * 100;
  return {
    z: Math.round(z * 100) / 100,
    percentile: Math.max(0.1, Math.min(99.9, Math.round(pct * 10) / 10)),
    L, M, S,
  };
};

export default percentile;
